import { createContext, use, useRef } from 'react';
import { useStore } from 'zustand';

import type { CreateQualityFormulaStoreOptions, QualityFormulaStore } from './qualityFormulaStore';

import { createQualityFormulaStore } from './qualityFormulaStore';

type QualityFormulaStoreApi = ReturnType<typeof createQualityFormulaStore>;

const QualityFormulaStoreContext = createContext<null | QualityFormulaStoreApi>(null);

export interface QualityFormulaStoreProviderProps extends CreateQualityFormulaStoreOptions {
  children: React.ReactNode;
}

/**
 * Scopes one `createQualityFormulaStore` instance to whatever subtree it wraps — Contract Entry's
 * playground (`QualityFormulasPage`) and the Template Editor (`TemplateEditorPage`) each render
 * their own provider, seeded from their own `initialSpecStates`/`initialVisibleSpecTypes`, so
 * neither workspace's state ever leaks into the other. The store itself is created once per mount
 * (via `useRef`, not `useState`) since it never needs to react to a prop change — both pages fully
 * remount this provider whenever they need a fresh workspace (e.g. navigating to a different
 * template).
 */
export const QualityFormulaStoreProvider: React.FC<QualityFormulaStoreProviderProps> = ({
  children,
  initialSpecStates,
  initialVisibleSpecTypes,
}) => {
  const storeRef = useRef<QualityFormulaStoreApi>(null);
  storeRef.current ??= createQualityFormulaStore({ initialSpecStates, initialVisibleSpecTypes });

  return <QualityFormulaStoreContext value={storeRef.current}>{children}</QualityFormulaStoreContext>;
};

/**
 * Reads from (or acts on) the nearest `QualityFormulaStoreProvider` — every component that used to
 * need `specType`-keyed handlers/state threaded in as props (`QualitySpecCard` and the page-level
 * Step 1/2/3 sections alike) instead calls this directly with a selector, so adding a new field a
 * deeply nested component needs never requires updating every component between it and the page.
 */
// eslint-disable-next-line comma-spacing -- Prettier's TSX generic syntax requires the trailing comma.
export const useQualityFormulaStore = <T,>(selector: (state: QualityFormulaStore) => T): T => {
  const store = use(QualityFormulaStoreContext);
  if (!store) {
    throw new Error('useQualityFormulaStore must be used within a QualityFormulaStoreProvider');
  }
  return useStore(store, selector);
};

/**
 * Returns the nearest `QualityFormulaStoreProvider`'s store API directly, for reading state (via
 * `.getState()`) in an event handler without subscribing the calling component to re-render on
 * every change — unlike `useQualityFormulaStore`, which always re-renders when its selected value
 * changes. Use this where a handler only needs a snapshot at call time (e.g. building a payload on
 * submit), not live state the component renders from.
 */
export const useQualityFormulaStoreApi = (): QualityFormulaStoreApi => {
  const store = use(QualityFormulaStoreContext);
  if (!store) {
    throw new Error('useQualityFormulaStoreApi must be used within a QualityFormulaStoreProvider');
  }
  return store;
};
